<template>
  <section class="auto-gallery-section">
    <div class="container">
      <div class="gallery-header">
        <div class="gallery-badge">
          <i :class="headerData.icon"></i>
          <span>{{ headerData.tag }}</span>
        </div>
        <h2>{{ headerData.title }}</h2>
        <p class="gallery-subtitle">{{ headerData.description }}</p>
      </div>
      
      <div class="auto-gallery-grid">
        <div class="gallery-item" v-for="(item, index) in galleryData" :key="index">
          <div class="gallery-image">
            <img :src="item.image" :alt="item.title" />
            <div class="gallery-overlay">
              <div class="overlay-content">
                <h3>{{ item.title }}</h3>
                <div class="service-stats" v-if="item.stats && item.stats.length > 0">
                  <div class="stat">
                    <span class="stat-value">{{ item.stats[0].value }}</span>
                    <span class="stat-label">{{ item.stats[0].label }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: "ServiceItsNormalGalleryComp",
  props: {
    headerData: {
      type: Object,
      required: true,
      default: () => ({
        icon: "fas fa-wrench",
        tag: "SERVICE GALLERY",
        title: "Auto Service Showcase",
        description: "See our automotive transformations and service excellence"
      })
    },
    galleryData: {
      type: Array,
      required: true,
      default: () => []
    }
  }
};
</script>

<style scoped>
/* Import Google Fonts */
@import url('https://fonts.googleapis.com/css2?family=Archivo:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&family=Open+Sans:ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;1,300;1,400;1,500;1,600;1,700;1,800&display=swap');

/* Import Flaticon */
@import '@/assets/scss/Flaticon.scss';

/* Auto Gallery Section */
.auto-gallery-section {
  padding: 100px 0;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
}

.gallery-header {
  text-align: center;
  margin-bottom: 60px;
}

.gallery-badge {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: linear-gradient(135deg, #dc3545, #c82333);
  color: white;
  padding: 8px 20px;
  border-radius: 25px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 20px;
}

.gallery-header h2 {
  font-size: 48px;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 20px;
  font-family: 'Archivo', sans-serif;
}

.gallery-subtitle {
  font-size: 18px;
  color: #6c757d;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
}

.auto-gallery-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
  margin-top: 50px;
}

.gallery-item {
  position: relative;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.gallery-item:hover {
  transform: translateY(-10px);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
}

.gallery-image {
  position: relative;
  overflow: hidden;
}

.gallery-image img {
  width: 100%;
  height: 250px;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.gallery-item:hover .gallery-image img {
  transform: scale(1.1);
}

.gallery-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(220, 53, 69, 0.95), rgba(200, 35, 51, 0.95));
  color: white;
  padding: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: all 0.3s ease;
  backdrop-filter: blur(5px);
}

.gallery-item:hover .gallery-overlay {
  opacity: 1;
}

.overlay-content {
  text-align: center;
  transform: translateY(15px);
  transition: transform 0.3s ease;
  max-width: 90%;
}

.gallery-item:hover .overlay-content {
  transform: translateY(0);
}

.overlay-content h3 {
  font-size: 22px;
  font-weight: 600;
  margin-bottom: 12px;
  font-family: 'Archivo', sans-serif;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.4);
  line-height: 1.3;
  word-wrap: break-word;
}

.service-stats {
  display: flex;
  justify-content: center;
  margin-top: 8px;
}

.stat {
  text-align: center;
  background: rgba(255, 255, 255, 0.25);
  padding: 10px 18px;
  border-radius: 25px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.4);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.stat:hover {
  background: rgba(255, 255, 255, 0.35);
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
}

.stat-value {
  display: block;
  font-size: 18px;
  font-weight: 700;
  color: white;
  margin-bottom: 2px;
  font-family: 'Archivo', sans-serif;
}

.stat-label {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.9);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 500;
}

/* Responsive Design */
@media (max-width: 768px) {
  .auto-gallery-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }
  
  .gallery-image img {
    height: 200px;
  }
  
  .overlay-content {
    max-width: 95%;
  }
  
  .overlay-content h3 {
    font-size: 18px;
    margin-bottom: 10px;
  }
  
  .stat {
    padding: 8px 14px;
  }
  
  .stat-value {
    font-size: 16px;
  }
  
  .stat-label {
    font-size: 10px;
  }
  
  .gallery-overlay {
    padding: 15px;
  }
  
  .service-stats {
    margin-top: 6px;
  }
}
</style>
